import type { ShowConsultation } from '@/components/PatientPageV2/components/ConsultationTabSections/ShowConsultation';
import { useConsultationTracking } from '@/components/PatientPageV2/hooks/useConsultationTracking';
import settings from '@/data/constants';
import { isDoctor } from '@/data/service/authService';
import type { OnSaveConsultationOptions } from '@/pages/Patient';
import { PrescriptionAssistantModal } from '@/shared-ui/PrescriptionAssistantModal/PrescriptionAssistantModal';
import { useConsultationStore } from '@/state-management/slices/consultations/hooks/useConsultationStore';
import { toast } from '@montugroup/design-system';
import { useModal } from 'mui-modal-provider';
import type { SaveConsultationParams } from './useSaveConsultationData';
import { useSaveConsultationData } from './useSaveConsultationData';

export type SaveConsultationOptions = {
  askConfirmation?: boolean;
  refetchData?: boolean;
  closeConsultation?: boolean;
  enableCustomOnSave?: boolean; // TODO: Remove this when the deprecated onSave prop
};

export type SaveConsultationFunc = (
  consultationData: SaveConsultationParams | null,
  options?: SaveConsultationOptions,
) => Promise<void>;

export type useSaveConsultationProps = {
  consultation: ShowConsultation | null;
  shouldUpdateConsultationStatus: boolean;
  onRefresh: () => void;
  onSaveComplete: (confirmed: boolean) => void;
  onSaveConsultation?: (params?: OnSaveConsultationOptions) => Promise<void>;
};

export const useSaveConsultation = ({
  consultation,
  shouldUpdateConsultationStatus,
  onRefresh,
  onSaveComplete,
  onSaveConsultation,
}: useSaveConsultationProps) => {
  const { saveConsultationData } = useSaveConsultationData();
  const { showModal } = useModal();
  const { isMarkedAsComplimentary } = useConsultationStore(consultation);

  const isDoc = isDoctor();
  const { trackCompleteConsultation } = useConsultationTracking();

  const makeSaveConsultationRequest = async (
    consultationData: SaveConsultationParams | null,
    options: Omit<SaveConsultationOptions, 'askConfirmation' | 'enableCustomOnSave'> = {
      refetchData: true,
      closeConsultation: false,
    },
  ) => {
    const { refetchData = true, closeConsultation = false } = options;

    const baseData = consultationData ?? {
      id: consultation?.id ?? 0,
      notes: consultation?.notes ?? '',
      status_id: consultation?.status_id ?? 0,
      sub_status_id: consultation?.sub_status_id ?? 0,
      isMarkedAsComplimentary,
    };

    const dataToSave = { ...baseData };

    return saveConsultationData(dataToSave).then((responseData) => {
      if (responseData) {
        onSaveComplete(closeConsultation);
        refetchData && onRefresh();
      }
    });
  };

  const showConfirmationModal = (onConfirm: () => void, onCancel?: () => void) => {
    const modal = showModal(PrescriptionAssistantModal, {
      title: 'Save Consultation',
      body: shouldUpdateConsultationStatus
        ? 'Are you sure to update the consultation status?'
        : 'Are you sure to proceed with the current consultation status?',
      severity: 'warning',
      confirmLabel: 'Save Consultation',
      closeLabel: 'Cancel',
      isLoading: false,
      onConfirm: () => {
        onConfirm();
        modal.hide();
      },
      onClose: () => {
        onCancel && onCancel();
        modal.hide();
      },
    });
  };

  const saveConsultationWithConfirmation = async (
    consultationData: SaveConsultationParams | null = null,
    options: Omit<SaveConsultationOptions, 'askConfirmation'> = {
      refetchData: false,
      closeConsultation: false,
      enableCustomOnSave: true,
    },
  ) => {
    if (!consultation) {
      return;
    }

    const { refetchData = false, closeConsultation = false, enableCustomOnSave = true } = options;

    return new Promise<void>((resolve, reject) => {
      const handleOnConfirm = async () => {
        if (isDoc && closeConsultation) {
          trackCompleteConsultation(consultation.id);
        }

        try {
          /**
          Adding the try catch here instead of onSaveConsultation to not modify any
          existing functionality as its used in a few others places
          that we are not touching at the moment.
        */
          if (onSaveConsultation && enableCustomOnSave) {
            await onSaveConsultation({ saveConsult: true });
          } else {
            await makeSaveConsultationRequest(consultationData ?? consultation, {
              refetchData,
              closeConsultation,
            });
          }
          resolve();
        } catch (err) {
          console.error(err);
          reject(err);
        }
      };

      showConfirmationModal(handleOnConfirm, resolve);
    });
  };

  const saveConsultation: SaveConsultationFunc = async (
    consultationData: SaveConsultationParams | null,
    options: SaveConsultationOptions = {
      askConfirmation: true,
      refetchData: false,
      closeConsultation: false,
      enableCustomOnSave: true,
    },
  ) => {
    if (!consultation) {
      return;
    }

    const {
      askConfirmation = true,
      refetchData = false,
      closeConsultation = false,
      enableCustomOnSave = true,
    } = options;

    if (!askConfirmation) {
      return await makeSaveConsultationRequest(consultationData, {
        refetchData,
        closeConsultation,
      });
    }

    if (consultationData?.status_id === settings.consultationStatus.ineligible && !consultationData?.sub_status_id) {
      toast.error({
        title: 'You must select an Ineligibility Reason',
        message: 'Please use the drop down to select the reason and then resubmit',
      });
      return;
    }

    await saveConsultationWithConfirmation(consultationData, {
      refetchData,
      closeConsultation,
      enableCustomOnSave,
    });
  };
  const submitNurseConsultation = async (consultationData: SaveConsultationParams | null) => {
    if (!consultation) {
      return;
    }
    if (consultationData?.status_id === settings.consultationStatus.ineligible && !consultationData?.sub_status_id) {
      toast.error({
        title: 'You must select an Ineligibility Reason',
        message: 'Please use the drop down to select the reason and then resubmit',
      });
      return;
    }

    await submitWithConfirmation();
  };
  const submitWithConfirmation = async () => {
    if (!consultation) {
      return;
    }

    const handleOnConfirm = async () => {
      try {
        await onSaveConsultation?.({ closeConsultation: true, skipAssessment: false });
      } catch (err) {
        console.error(err);
      }
    };

    showConfirmationModal(handleOnConfirm);
  };
  return {
    saveConsultation,
    saveConsultationWithConfirmation,
    submitNurseConsultation,
  };
};
